/**
 * Table utility functions for sorting, exporting, and data processing
 */

export type SortDirection = "asc" | "desc";
export type DataType = "string" | "number" | "date" | "boolean";

export interface SortConfig {
  column: string;
  direction: SortDirection;
}

export interface TableData {
  headers: string[];
  rows: Array<Record<string, any>>;
  totalRows: number;
  displayedRows: number;
}

/**
 * Detect the data type of a column based on its values
 */
export function detectColumnDataType(values: any[]): DataType {
  if (values.length === 0) return "string";

  // Filter out null/undefined values for type detection
  const validValues = values.filter((val) => val != null && val !== "");

  if (validValues.length === 0) return "string";

  // Check if all values are booleans
  if (validValues.every((val) => typeof val === "boolean")) {
    return "boolean";
  }

  // Check if all values look like numbers
  const numberPattern = /^-?\d+\.?\d*$/;

  if (validValues.every((val) => numberPattern.test(String(val).trim()))) {
    return "number";
  }

  // Check if all values look like dates
  const datePattern = /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{2}-\d{2}-\d{4}/;

  if (validValues.every((val) => datePattern.test(String(val).trim()))) {
    return "date";
  }

  return "string";
}

/**
 * Sort table data by a specific column
 */
export function sortTableData(data: any[], column: string, direction: SortDirection): any[] {
  return [...data].sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    // Handle null/undefined values (put them at the end)
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Detect data type for this column
    const dataType = detectColumnDataType([aVal, bVal]);
    let comparison = 0;

    switch (dataType) {
      case "number":
        comparison = Number(aVal) - Number(bVal);
        break;
      case "date":
        comparison = new Date(aVal).getTime() - new Date(bVal).getTime();
        break;
      case "boolean":
        comparison = Number(aVal) - Number(bVal);
        break;
      default:
        comparison = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
          sensitivity: "base",
        });
    }

    return direction === "asc" ? comparison : -comparison;
  });
}

/**
 * Parse table content and extract structured data
 * Supports JSON, TSV (tab-separated), and markdown pipe table formats
 */
export function parseTableContent(content: string): TableData | null {
  // Try parsing as JSON first
  try {
    const jsonData = JSON.parse(content.trim());

    if (Array.isArray(jsonData) && jsonData.length > 0) {
      const firstItem = jsonData[0];
      const headers = Object.keys(firstItem);

      return {
        headers,
        rows: jsonData,
        totalRows: jsonData.length,
        displayedRows: jsonData.length,
      };
    }
  } catch {
    // Not valid JSON, try other formats
  }

  // Try parsing as TSV (tab-separated values)
  if (content.includes("\t")) {
    const lines = content.trim().split("\n");

    if (lines.length >= 2) {
      const headers = lines[0].split("\t").map((h) => h.trim());
      const rows: Array<Record<string, any>> = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split("\t");

        if (values.length === headers.length) {
          const row: Record<string, any> = {};

          headers.forEach((header, index) => {
            row[header] = values[index].trim();
          });
          rows.push(row);
        }
      }

      if (rows.length > 0) {
        return {
          headers,
          rows,
          totalRows: rows.length,
          displayedRows: rows.length,
        };
      }
    }
  }

  // Try parsing as markdown pipe table
  if (content.includes("|")) {
    const lines = content
      .trim()
      .split("\n")
      .filter((line) => line.trim().length > 0);

    if (lines.length >= 3) {
      // Parse header row
      const headerLine = lines[0];
      const headers = headerLine
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h.length > 0);

      // Skip separator line (lines[1])
      const rows: Array<Record<string, any>> = [];

      for (let i = 2; i < lines.length; i++) {
        // Use same parsing logic as headers: split, trim, filter empty strings
        const values = lines[i]
          .split("|")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);

        if (values.length > 0) {
          const row: Record<string, any> = {};

          headers.forEach((header, index) => {
            row[header] = index < values.length ? values[index] : "";
          });
          rows.push(row);
        }
      }

      if (rows.length > 0) {
        return {
          headers,
          rows,
          totalRows: rows.length,
          displayedRows: rows.length,
        };
      }
    }
  }

  return null;
}

/**
 * Convert table data to CSV format
 */
export function tableToCSV(headers: string[], rows: Array<Record<string, any>>): string {
  // Helper function to escape CSV values
  const escapeCSVValue = (value: any): string => {
    const str = String(value || "");

    // If the value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
      return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  };

  // Create CSV header row
  const csvHeaders = headers.map(escapeCSVValue).join(",");

  // Create CSV data rows
  const csvRows = rows.map((row) => headers.map((header) => escapeCSVValue(row[header])).join(","));

  return [csvHeaders, ...csvRows].join("\n");
}

/**
 * Convert table data to JSON format (formatted)
 */
export function tableToJSON(rows: Array<Record<string, any>>): string {
  return JSON.stringify(rows, null, 2);
}

/**
 * Convert table data to Markdown pipe table format
 * This creates a properly formatted markdown table that can be converted to Word/PDF
 */
export function tableToMarkdown(headers: string[], rows: Array<Record<string, any>>): string {
  if (headers.length === 0) return "";

  // Create header row
  const headerRow = `| ${headers.join(" | ")} |`;

  // Create separator row (with dashes)
  const separatorRow = `| ${headers.map(() => "---").join(" | ")} |`;

  // Create data rows
  const dataRows = rows.map((row) => {
    const values = headers.map((header) => {
      const value = row[header];

      // Escape pipe characters in cell values and handle null/undefined
      return String(value ?? "").replace(/\|/g, "\\|");
    });

    return `| ${values.join(" | ")} |`;
  });

  return [headerRow, separatorRow, ...dataRows].join("\n");
}

/**
 * Get column data types for all headers
 */
export function getColumnDataTypes(
  headers: string[],
  rows: Array<Record<string, any>>,
): Record<string, DataType> {
  const dataTypes: Record<string, DataType> = {};

  headers.forEach((header) => {
    const columnValues = rows.map((row) => row[header]);

    dataTypes[header] = detectColumnDataType(columnValues);
  });

  return dataTypes;
}

/**
 * Get table dimensions as formatted string
 */
export function getTableDimensions(tableData: TableData): string {
  const { headers, totalRows } = tableData;

  return `${headers.length} columns × ${totalRows} rows`;
}

/**
 * Strip markdown formatting from text
 * Converts [text](url) to just text, removes bold/italic markers
 */
function stripMarkdown(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Strip markdown links
    .replace(/\*+([^*]+)\*+/g, "$1") // Strip bold/italic
    .replace(/_+([^_]+)_+/g, "$1") // Strip underscores
    .replace(/`([^`]+)`/g, "$1"); // Strip inline code
}

/**
 * Generate a preview of table data (first few rows)
 * Returns comma-separated values for first row only
 */
export function getTablePreview(
  headers: string[],
  rows: Array<Record<string, any>>,
  _maxRows: number = 3,
): string {
  if (rows.length === 0) return "Empty table";

  // Show first row values only, comma-separated
  const firstRow = rows[0];
  const values = headers
    .map((header) => {
      // Strip markdown and truncate
      const value = stripMarkdown(String(firstRow[header] || "")).trim();

      return value.length > 25 ? value.substring(0, 22) + "..." : value;
    })
    .filter((v) => v.length > 0);

  return values.join(", ");
}
